import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Hook que padroniza o sticky header + sticky thead em telas com tabela longa.
 *
 * Uso:
 *   const sticky = useStickyHeader();
 *   <div ref={sticky.headerRef} style={sticky.headerStyle}>...header...</div>
 *   <table style={sticky.tableStyle}>
 *     <thead><tr><th style={sticky.thStyle}>...</th></tr></thead>
 *
 * Detalhes:
 *  - Mede a altura do header com offsetHeight (border-box, inclui padding) — ResizeObserver.contentRect.height
 *    ignora o padding e o thead colaria no meio do header.
 *  - Zera padding-top/bottom do .app-content enquanto a pagina esta montada (restaura no unmount).
 *  - useLayoutEffect mede sincronamente antes do paint pra evitar flash com headerH=0.
 *  - O wrapper sticky usa marginX:-20 paddingX:20 pra esticar pra fora do padding lateral
 *    do .app-content e cobrir a area inteira quando colado.
 *  - tableStyle aplica `border-collapse: separate; border-spacing: 0; overflow: visible` —
 *    a classe global .tabela usa overflow:hidden que bloqueia sticky em descendentes.
 */
export function useStickyHeader(initialHeaderHeight = 80) {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerH, setHeaderH] = useState(initialHeaderHeight);

  useLayoutEffect(() => {
    if (headerRef.current) setHeaderH(headerRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    if (!headerRef.current) return;
    const ro = new ResizeObserver(() => {
      if (headerRef.current) setHeaderH(headerRef.current.offsetHeight);
    });
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const main = document.querySelector('.app-content') as HTMLElement | null;
    if (!main) return;
    const origTop = main.style.paddingTop;
    const origBottom = main.style.paddingBottom;
    main.style.paddingTop = '0';
    main.style.paddingBottom = '0';
    return () => {
      main.style.paddingTop = origTop;
      main.style.paddingBottom = origBottom;
    };
  }, []);

  const headerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    background: 'var(--page-bg)',
    marginLeft: -20,
    marginRight: -20,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 8,
    paddingBottom: 8,
  };

  const thStyle: React.CSSProperties = {
    position: 'sticky',
    top: headerH,
    background: 'var(--grid-header-bg)',
    // Aplica tambem a cor da fonte do cabecalho (Configuracoes > Cores >
    // "Cabecalhos de tabela" > "Principal — fonte"). Sem isso, tabelas que NAO
    // usam a classe global .tabela (ex: Lista de Compras) herdavam a cor escura
    // da pagina e o titulo sumia sobre o fundo escuro do cabecalho.
    color: 'var(--grid-header-fg)',
    zIndex: 10,
  };

  const tableStyle: React.CSSProperties = {
    borderCollapse: 'separate',
    borderSpacing: 0,
    overflow: 'visible',
  };

  return { headerRef, headerH, headerStyle, thStyle, tableStyle };
}
